import React, { useEffect, useMemo, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { AlignLeft, Edit2, Eye, EyeOff, Image as ImageIcon, Plus, Save, Trash2, Type, X } from 'lucide-react';
import api, { assetUrl, serviceCategoryApi, serviceContentApi } from '../../../services/api';

interface Service {
  id: string;
  title: string;
  subtitle: string;
  description: string;
  image: string;
  slug: string;
  category: string;
  isActive: boolean;
  displayOrder: number;
  features: string[];
}

interface ServiceCategory {
  id: string;
  name: string;
  description: string;
  image: string;
  isActive: boolean;
  displayOrder: number;
}

type ServicesHeroSettings = {
  badge_text: string;
  heading: string;
  description: string;
  button_text: string;
};

const DEFAULT_HERO_SETTINGS: ServicesHeroSettings = {
  badge_text: 'Services',
  heading: 'OUR EXPERTISE',
  description:
    "We don't just build structures; we create environments that inspire and engage. Discover our core specializations.",
  button_text: 'Explore Service',
};

const EMPTY_SERVICE: Service = {
  id: '',
  title: '',
  subtitle: '',
  description: '',
  image: '',
  slug: '',
  category: 'Exhibition',
  isActive: true,
  displayOrder: 0,
  features: [],
};

const EMPTY_CATEGORY: ServiceCategory = {
  id: '',
  name: '',
  description: '',
  image: '',
  isActive: true,
  displayOrder: 0,
};

const ModernServicesEditor: React.FC<{ services?: any[]; onUpdate?: () => void }> = ({ services, onUpdate }) => {
  const [serviceList, setServiceList] = useState<Service[]>([]);
  const [categoryList, setCategoryList] = useState<ServiceCategory[]>([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isCategoryModalOpen, setIsCategoryModalOpen] = useState(false);
  const [editingService, setEditingService] = useState<Service | null>(null);
  const [editingCategory, setEditingCategory] = useState<ServiceCategory | null>(null);
  const [formData, setFormData] = useState<Service>(EMPTY_SERVICE);
  const [categoryFormData, setCategoryFormData] = useState<ServiceCategory>(EMPTY_CATEGORY);
  const [newFeature, setNewFeature] = useState('');
  const [uploadingImage, setUploadingImage] = useState(false);
  const [uploadingCategoryImage, setUploadingCategoryImage] = useState(false);
  const [heroSettings, setHeroSettings] = useState<ServicesHeroSettings>(DEFAULT_HERO_SETTINGS);

  useEffect(() => {
    if (!isModalOpen && !isCategoryModalOpen) return;

    const scrollToEditor = () => {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    scrollToEditor();
    const frameId = window.requestAnimationFrame(scrollToEditor);
    return () => window.cancelAnimationFrame(frameId);
  }, [isModalOpen, isCategoryModalOpen]);

  useEffect(() => {
    const load = async () => {
      try {
        const res = await api.serviceApi.getAll(false);
        const list = (res?.data || []).map((s: any) => ({
          id: String(s.id),
          title: String(s.title || ''),
          subtitle: String(s.subtitle || s.category || ''),
          description: String(s.description || ''),
          image: assetUrl(s.featured_image || s.image),
          slug: String(s.detail_page_slug || s.slug || ''),
          category: String(s.category || 'Exhibition'),
          isActive: !!s.is_active,
          displayOrder: s.display_order || s.order || 0,
          features: Array.isArray(s.features) ? s.features : [],
        }));
        setServiceList(list);
      } catch {
        setServiceList([]);
      }

      try {
        const categoryRes = await serviceCategoryApi.getAll(false);
        setCategoryList(
          (categoryRes?.data || []).map((category: any) => ({
            id: String(category.id),
            name: String(category.name || ''),
            description: String(category.description || ''),
            image: assetUrl(category.image),
            isActive: !!category.is_active,
            displayOrder: Number(category.display_order || 0),
          }))
        );
      } catch {
        setCategoryList([]);
      }

      try {
        const headingRes = await serviceContentApi.get();
        if (headingRes?.success && headingRes.data) {
          setHeroSettings({
            badge_text: String(headingRes.data.badge_text || DEFAULT_HERO_SETTINGS.badge_text),
            heading: String(headingRes.data.heading || DEFAULT_HERO_SETTINGS.heading),
            description: String(headingRes.data.description || DEFAULT_HERO_SETTINGS.description),
            button_text: String(headingRes.data.button_text || DEFAULT_HERO_SETTINGS.button_text),
          });
        } else {
          setHeroSettings(DEFAULT_HERO_SETTINGS);
        }
      } catch {
        setHeroSettings(DEFAULT_HERO_SETTINGS);
      }
    };

    load();
  }, [services]);

  const sortedServices = useMemo(
    () => [...serviceList].sort((a, b) => a.displayOrder - b.displayOrder),
    [serviceList]
  );
  const sortedCategories = useMemo(
    () => [...categoryList].sort((a, b) => a.displayOrder - b.displayOrder),
    [categoryList]
  );
  const categoryOptions = useMemo(() => {
    const values = [...sortedCategories.map((category) => category.name), ...serviceList.map((service) => service.category), formData.category];
    return Array.from(new Set(values.filter(Boolean)));
  }, [sortedCategories, serviceList, formData.category]);

  const handleSaveHeroSettings = async () => {
    try {
      const res = await serviceContentApi.update({
        badge_text: heroSettings.badge_text,
        heading: heroSettings.heading,
        description: heroSettings.description,
        button_text: heroSettings.button_text,
      });
      if (res?.success) {
        window.dispatchEvent(new CustomEvent('services:updated'));
        onUpdate?.();
        alert('Services section content updated');
      }
    } catch {
      alert('Failed to update services section content');
    }
  };

  const handleAdd = () => {
    setEditingService(null);
    setFormData({
      ...EMPTY_SERVICE,
      id: Date.now().toString(),
      subtitle: 'Exhibition',
      displayOrder: serviceList.length,
    });
    setNewFeature('');
    setIsModalOpen(true);
  };

  const handleAddCategory = () => {
    setEditingCategory(null);
    setCategoryFormData({
      ...EMPTY_CATEGORY,
      id: Date.now().toString(),
      displayOrder: categoryList.length,
    });
    setIsCategoryModalOpen(true);
  };

  const handleEdit = (service: Service) => {
    setEditingService(service);
    setFormData(service);
    setNewFeature('');
    setIsModalOpen(true);
  };

  const handleEditCategory = (category: ServiceCategory) => {
    setEditingCategory(category);
    setCategoryFormData(category);
    setIsCategoryModalOpen(true);
  };

  const handleDelete = async (id: string) => {
    try {
      await api.serviceApi.delete(Number(id));
      setServiceList((current) => current.filter((service) => service.id !== id));
      window.dispatchEvent(new CustomEvent('services:updated'));
      onUpdate?.();
      alert('Service deleted');
    } catch {
      alert('Error deleting service');
    }
  };

  const handleDeleteCategory = async (id: string) => {
    try {
      await serviceCategoryApi.delete(Number(id));
      setCategoryList((current) => current.filter((category) => category.id !== id));
      window.dispatchEvent(new CustomEvent('services:updated'));
      onUpdate?.();
      alert('Category deleted');
    } catch (error) {
      alert(error instanceof Error ? error.message : 'Error deleting category');
    }
  };

  const handleToggleActive = async (id: string) => {
    try {
      const current = serviceList.find((service) => service.id === id);
      const nextValue = !(current?.isActive);
      await api.serviceApi.update(Number(id), { is_active: nextValue });
      setServiceList((currentServices) =>
        currentServices.map((service) => (service.id === id ? { ...service, isActive: nextValue } : service))
      );
      window.dispatchEvent(new CustomEvent('services:updated'));
      onUpdate?.();
    } catch {
      alert('Error updating status');
    }
  };

  const handleToggleCategoryActive = async (id: string) => {
    try {
      const current = categoryList.find((category) => category.id === id);
      if (!current) return;
      const nextValue = !current.isActive;
      await serviceCategoryApi.update(Number(id), { is_active: nextValue });
      setCategoryList((currentCategories) =>
        currentCategories.map((category) => (category.id === id ? { ...category, isActive: nextValue } : category))
      );
      window.dispatchEvent(new CustomEvent('services:updated'));
      onUpdate?.();
    } catch (error) {
      alert(error instanceof Error ? error.message : 'Error updating category status');
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const payload = {
      title: formData.title,
      subtitle: formData.category,
      description: formData.description,
      category: formData.category,
      icon: editingService ? undefined : null,
      featured_image: formData.image,
      detail_page_slug: formData.slug || null,
      is_active: formData.isActive,
      order: formData.displayOrder,
      features: formData.features,
    };

    try {
      if (editingService) {
        await api.serviceApi.update(Number(formData.id), payload);
        setServiceList((current) => current.map((service) => (service.id === formData.id ? { ...formData } : service)));
      } else {
        const res = await api.serviceApi.create(payload);
        const newId = String(res?.data?.id || Date.now());
        setServiceList((current) => [...current, { ...formData, id: newId }]);
      }

      window.dispatchEvent(new CustomEvent('services:updated'));
      setIsModalOpen(false);
      setEditingService(null);
      onUpdate?.();
      alert(editingService ? 'Service updated' : 'Service created');
    } catch (error) {
      alert(error instanceof Error ? error.message : 'Error saving service');
    }
  };

  const handleCategorySubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const payload = {
      name: categoryFormData.name,
      description: categoryFormData.description,
      image: categoryFormData.image,
      is_active: categoryFormData.isActive,
      display_order: categoryFormData.displayOrder,
    };

    try {
      if (editingCategory) {
        await serviceCategoryApi.update(Number(categoryFormData.id), payload);
        setCategoryList((current) =>
          current.map((category) => (category.id === categoryFormData.id ? { ...categoryFormData } : category))
        );
      } else {
        const res = await serviceCategoryApi.create(payload);
        const newId = String(res?.data?.id || Date.now());
        setCategoryList((current) => [...current, { ...categoryFormData, id: newId }]);
      }

      window.dispatchEvent(new CustomEvent('services:updated'));
      setIsCategoryModalOpen(false);
      setEditingCategory(null);
      onUpdate?.();
      alert(editingCategory ? 'Category updated' : 'Category created');
    } catch (error) {
      alert(error instanceof Error ? error.message : 'Error saving category');
    }
  };

  const handleAddFeature = () => {
    if (!newFeature.trim()) return;
    setFormData((current) => ({
      ...current,
      features: [...current.features, newFeature.trim()],
    }));
    setNewFeature('');
  };

  const handleRemoveFeature = (index: number) => {
    setFormData((current) => ({
      ...current,
      features: current.features.filter((_, currentIndex) => currentIndex !== index),
    }));
  };

  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploadingImage(true);
    try {
      if (!editingService) {
        const uploadFormData = new FormData();
        uploadFormData.append('file', file);

        const response = await fetch('/api/upload', {
          method: 'POST',
          body: uploadFormData,
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`,
          },
        });

        if (!response.ok) {
          const error = await response.json();
          throw new Error(error.message || 'Image upload failed');
        }

        const result = await response.json();
        const uploadedUrl =
          result?.url ||
          result?.imageUrl ||
          result?.data?.url ||
          result?.data?.imageUrl ||
          result?.data?.featured_image ||
          '';

        if (!uploadedUrl) {
          throw new Error('Upload response missing image URL');
        }

        setFormData((current) => ({ ...current, image: uploadedUrl }));
      } else {
        const uploadFormData = new FormData();
        uploadFormData.append('image', file);

        const result = await api.serviceApi.uploadImage(Number(formData.id), uploadFormData);
        const uploadedUrl =
          result?.imageUrl ||
          result?.url ||
          result?.data?.featured_image ||
          result?.data?.image_url ||
          result?.data?.url ||
          '';

        if (!uploadedUrl) {
          throw new Error('Upload response missing image URL');
        }

        setFormData((current) => ({ ...current, image: uploadedUrl }));
      }
    } catch (error) {
      console.error('Upload error:', error);
      alert('Error uploading image');
    } finally {
      setUploadingImage(false);
    }
  };

  const handleCategoryImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploadingCategoryImage(true);
    try {
      const uploadFormData = new FormData();
      uploadFormData.append('file', file);

      const response = await fetch('/api/upload', {
        method: 'POST',
        body: uploadFormData,
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || 'Image upload failed');
      }

      const result = await response.json();
      const uploadedUrl =
        result?.url ||
        result?.imageUrl ||
        result?.data?.url ||
        result?.data?.imageUrl ||
        '';

      if (!uploadedUrl) {
        throw new Error('Upload response missing image URL');
      }

      setCategoryFormData((current) => ({ ...current, image: uploadedUrl }));
    } catch (error) {
      console.error('Category upload error:', error);
      alert('Error uploading category image');
    } finally {
      setUploadingCategoryImage(false);
    }
  };

  return (
    <div className="space-y-6 p-6">
      <div className="space-y-4 rounded-2xl border border-white/10 bg-[#0b0c15]/50 p-6">
        <div className="flex items-center justify-between gap-4">
          <div>
            <h2 className="text-2xl font-bold text-white">Services Hero Box</h2>
            <p className="text-sm text-gray-400">Front page services section ka heading, paragraph, right-side label aur card button text yahan se manage hoga.</p>
          </div>
          <button
            type="button"
            onClick={handleSaveHeroSettings}
            className="flex items-center gap-2 rounded-lg bg-green-600 px-4 py-2 text-sm text-white hover:bg-green-700"
          >
            <Save size={16} />
            Save Content
          </button>
        </div>
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <input
            value={heroSettings.heading}
            onChange={(e) => setHeroSettings((current) => ({ ...current, heading: e.target.value }))}
            className="bg-[#1a1a2e] border border-white/10 rounded-lg px-3 py-2 text-white md:col-span-2"
            placeholder="Main heading"
          />
          <input
            value={heroSettings.badge_text}
            onChange={(e) => setHeroSettings((current) => ({ ...current, badge_text: e.target.value }))}
            className="bg-[#1a1a2e] border border-white/10 rounded-lg px-3 py-2 text-white"
            placeholder="Right label"
          />
          <input
            value={heroSettings.button_text}
            onChange={(e) => setHeroSettings((current) => ({ ...current, button_text: e.target.value }))}
            className="bg-[#1a1a2e] border border-white/10 rounded-lg px-3 py-2 text-white"
            placeholder="Card button text"
          />
          <textarea
            value={heroSettings.description}
            onChange={(e) => setHeroSettings((current) => ({ ...current, description: e.target.value }))}
            className="min-h-[120px] bg-[#1a1a2e] border border-white/10 rounded-lg px-3 py-2 text-white md:col-span-2"
            placeholder="Section description"
          />
        </div>
      </div>

      <div className="space-y-5 rounded-2xl border border-white/10 bg-[#0b0c15]/50 p-6">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold text-white">Services Category Section</h2>
            <p className="text-sm text-gray-400">Category, description aur image yahan save hongi. Ye front page par category cards ki tarah show hongi.</p>
          </div>
          <button
            onClick={handleAddCategory}
            className="flex items-center gap-2 rounded-lg bg-orange-500 px-4 py-2 text-white transition-colors hover:bg-orange-600"
          >
            <Plus size={18} />
            Add Category
          </button>
        </div>

        <div className="grid grid-cols-1 gap-5 md:grid-cols-2 xl:grid-cols-3">
          {sortedCategories.map((category) => (
            <div
              key={category.id}
              className={`overflow-hidden rounded-2xl border ${
                category.isActive ? 'border-orange-500/30 bg-white/[0.03]' : 'border-white/10 bg-white/[0.02] opacity-60'
              }`}
            >
              <div className="relative h-48 overflow-hidden">
                <img src={category.image} alt={category.name} className="h-full w-full object-cover" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 to-transparent" />
              </div>
              <div className="space-y-4 p-5">
                <div>
                  <p className="text-xs uppercase tracking-[0.28em] text-orange-400">Category</p>
                  <h3 className="mt-2 text-2xl font-bold text-white">{category.name}</h3>
                </div>
                <p className="line-clamp-3 text-sm text-gray-400">{category.description}</p>
                <div className="flex gap-2">
                  <button
                    onClick={() => handleEditCategory(category)}
                    className="flex flex-1 items-center justify-center gap-1 rounded-lg border border-blue-500/30 bg-blue-500/20 px-3 py-2 text-blue-400 transition-colors hover:bg-blue-500/30"
                  >
                    <Edit2 size={14} />
                    Edit
                  </button>
                  <button
                    onClick={() => handleToggleCategoryActive(category.id)}
                    className={`flex items-center justify-center rounded-lg px-3 py-2 transition-colors ${
                      category.isActive
                        ? 'border border-gray-500/30 bg-gray-500/20 text-gray-300 hover:bg-gray-500/30'
                        : 'border border-green-500/30 bg-green-500/20 text-green-400 hover:bg-green-500/30'
                    }`}
                  >
                    {category.isActive ? <EyeOff size={14} /> : <Eye size={14} />}
                  </button>
                  <button
                    onClick={() => handleDeleteCategory(category.id)}
                    className="rounded-lg border border-red-500/30 bg-red-500/20 px-3 py-2 text-red-400 transition-colors hover:bg-red-500/30"
                  >
                    <Trash2 size={14} />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="flex items-center justify-between">
        <div>
          <h1 className="mb-2 text-3xl font-bold text-white">Services</h1>
          <p className="text-gray-400">Cards ka title, subtitle, image, icon aur ordering yahan se manage karein.</p>
        </div>
        <button
          onClick={handleAdd}
          className="flex items-center gap-2 rounded-lg bg-orange-500 px-4 py-2 text-white transition-colors hover:bg-orange-600"
        >
          <Plus size={18} />
          Add Service
        </button>
      </div>

        <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
          {sortedServices.map((service, index) => {
          return (
            <motion.div
              key={service.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.08 }}
              className={`overflow-hidden rounded-2xl border transition-all duration-300 ${
                service.isActive
                  ? 'border-orange-500/40 bg-gradient-to-br from-orange-500/10 to-[#10131f]'
                  : 'border-gray-700/50 bg-gray-800/50 opacity-60'
              }`}
            >
              <div className="relative aspect-video overflow-hidden">
                <img src={service.image} alt={service.title} className="h-full w-full object-cover" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/30 to-transparent" />
                <div className="absolute right-4 top-4">
                  <span
                    className={`rounded-full border px-3 py-1 text-xs font-medium ${
                      service.isActive
                        ? 'border-green-500/30 bg-green-500/20 text-green-400'
                        : 'border-gray-500/30 bg-gray-500/20 text-gray-400'
                    }`}
                  >
                    {service.isActive ? 'Active' : 'Inactive'}
                  </span>
                </div>
              </div>

              <div className="space-y-4 p-6">
                <div>
                  <p className="text-xs uppercase tracking-[0.28em] text-orange-400">{service.category}</p>
                  <h3 className="mt-2 text-2xl font-bold text-white">{service.title}</h3>
                </div>
                <p className="line-clamp-3 text-sm text-gray-400">{service.description}</p>

                {service.features.length > 0 && (
                  <div className="flex flex-wrap gap-2">
                    {service.features.slice(0, 3).map((feature, featureIndex) => (
                      <span key={`${service.id}-${featureIndex}`} className="rounded-full bg-white/10 px-3 py-1 text-xs text-gray-200">
                        {feature}
                      </span>
                    ))}
                    {service.features.length > 3 && (
                      <span className="rounded-full bg-white/10 px-3 py-1 text-xs text-gray-400">
                        +{service.features.length - 3}
                      </span>
                    )}
                  </div>
                )}

                <div className="flex gap-2">
                  <button
                    onClick={() => handleEdit(service)}
                    className="flex flex-1 items-center justify-center gap-1 rounded-lg border border-blue-500/30 bg-blue-500/20 px-3 py-2 text-blue-400 transition-colors hover:bg-blue-500/30"
                  >
                    <Edit2 size={14} />
                    Edit
                  </button>
                  <button
                    onClick={() => handleToggleActive(service.id)}
                    className={`flex items-center justify-center rounded-lg px-3 py-2 transition-colors ${
                      service.isActive
                        ? 'border border-gray-500/30 bg-gray-500/20 text-gray-300 hover:bg-gray-500/30'
                        : 'border border-green-500/30 bg-green-500/20 text-green-400 hover:bg-green-500/30'
                    }`}
                  >
                    {service.isActive ? <EyeOff size={14} /> : <Eye size={14} />}
                  </button>
                  <button
                    onClick={() => handleDelete(service.id)}
                    className="rounded-lg border border-red-500/30 bg-red-500/20 px-3 py-2 text-red-400 transition-colors hover:bg-red-500/30"
                  >
                    <Trash2 size={14} />
                  </button>
                </div>
              </div>
            </motion.div>
          );
        })}
      </div>

      <AnimatePresence>
        {isCategoryModalOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[9999] flex items-start justify-center bg-black/80 p-4 pt-6 backdrop-blur-sm md:pt-10"
            onClick={() => setIsCategoryModalOpen(false)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="relative z-[9998] w-full max-w-2xl rounded-2xl border border-gray-800 bg-gray-900 p-6 shadow-2xl shadow-black/50"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="mb-6 flex items-center justify-between">
                <h2 className="text-2xl font-bold text-white">{editingCategory ? 'Edit Category' : 'Add Category'}</h2>
                <button onClick={() => setIsCategoryModalOpen(false)} className="rounded-lg p-2 transition-colors hover:bg-gray-800">
                  <X size={20} className="text-gray-400" />
                </button>
              </div>

              <form onSubmit={handleCategorySubmit} className="space-y-6">
                <div>
                  <label className="mb-2 block text-sm font-medium text-gray-300">Category Name</label>
                  <input
                    type="text"
                    value={categoryFormData.name}
                    onChange={(e) => setCategoryFormData((current) => ({ ...current, name: e.target.value }))}
                    className="w-full rounded-lg border border-gray-700 bg-gray-800 px-4 py-3 text-white placeholder-gray-500 focus:border-orange-500 focus:outline-none"
                    placeholder="Exhibition"
                    required
                  />
                </div>

                <div>
                  <label className="mb-2 block text-sm font-medium text-gray-300">Category Description</label>
                  <textarea
                    value={categoryFormData.description}
                    onChange={(e) => setCategoryFormData((current) => ({ ...current, description: e.target.value }))}
                    className="min-h-[120px] w-full rounded-lg border border-gray-700 bg-gray-800 px-4 py-3 text-white placeholder-gray-500 focus:border-orange-500 focus:outline-none"
                    placeholder="Category description"
                    required
                  />
                </div>

                <div>
                  <label className="mb-2 flex items-center gap-2 text-sm font-medium text-gray-300">
                    <ImageIcon size={16} />
                    Category Image
                  </label>
                  <div className="space-y-3">
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleCategoryImageUpload}
                      disabled={uploadingCategoryImage}
                      className="w-full cursor-pointer rounded-lg border border-gray-700 bg-gray-800 px-4 py-3 text-gray-300 file:mr-4 file:rounded-lg file:border-0 file:bg-orange-500 file:px-4 file:py-2 file:text-sm file:font-medium file:text-white hover:file:bg-orange-600 disabled:cursor-not-allowed disabled:opacity-50"
                    />
                    {uploadingCategoryImage && <div className="text-sm text-orange-400">Uploading...</div>}
                    {categoryFormData.image && (
                      <img
                        src={categoryFormData.image.startsWith('blob:') ? categoryFormData.image : assetUrl(categoryFormData.image)}
                        alt="Category Preview"
                        className="h-32 w-32 rounded-lg border border-gray-700 object-cover"
                      />
                    )}
                  </div>
                </div>

                <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
                  <div>
                    <label className="mb-2 block text-sm font-medium text-gray-300">Display Order</label>
                    <input
                      type="number"
                      value={categoryFormData.displayOrder}
                      onChange={(e) =>
                        setCategoryFormData((current) => ({ ...current, displayOrder: parseInt(e.target.value, 10) || 0 }))
                      }
                      className="w-full rounded-lg border border-gray-700 bg-gray-800 px-4 py-3 text-white placeholder-gray-500 focus:border-orange-500 focus:outline-none"
                    />
                  </div>

                  <div className="flex items-center gap-3 pt-8">
                    <input
                      type="checkbox"
                      id="categoryIsActive"
                      checked={categoryFormData.isActive}
                      onChange={(e) => setCategoryFormData((current) => ({ ...current, isActive: e.target.checked }))}
                      className="h-4 w-4 rounded border-gray-600 bg-gray-800 text-orange-500 focus:ring-orange-500"
                    />
                    <label htmlFor="categoryIsActive" className="text-sm text-gray-300">
                      Active on front page
                    </label>
                  </div>
                </div>

                <div className="flex gap-3 pt-2">
                  <button
                    type="button"
                    onClick={() => setIsCategoryModalOpen(false)}
                    className="flex-1 rounded-lg border border-gray-700 bg-gray-800 px-4 py-3 text-gray-300 transition-colors hover:bg-gray-700"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="flex flex-1 items-center justify-center gap-2 rounded-lg bg-orange-500 px-4 py-3 text-white transition-colors hover:bg-orange-600"
                  >
                    <Save size={18} />
                    {editingCategory ? 'Update Category' : 'Create Category'}
                  </button>
                </div>
              </form>
            </motion.div>
          </motion.div>
        )}

        {isModalOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[9999] flex items-start justify-center bg-black/80 p-4 pt-6 backdrop-blur-sm md:pt-10"
            onClick={() => setIsModalOpen(false)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="relative z-[9998] h-[96vh] max-h-[96vh] w-full max-w-3xl overflow-y-auto rounded-2xl border border-gray-800 bg-gray-900 p-6 shadow-2xl shadow-black/50"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="mb-6 flex items-center justify-between">
                <h2 className="text-2xl font-bold text-white">{editingService ? 'Edit Service' : 'Add Service'}</h2>
                <button onClick={() => setIsModalOpen(false)} className="rounded-lg p-2 transition-colors hover:bg-gray-800">
                  <X size={20} className="text-gray-400" />
                </button>
              </div>

              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
                  <div>
                    <label className="mb-2 flex items-center gap-2 text-sm font-medium text-gray-300">
                      <Type size={16} />
                      Title
                    </label>
                    <input
                      type="text"
                      value={formData.title}
                      onChange={(e) => setFormData((current) => ({ ...current, title: e.target.value }))}
                      className="w-full rounded-lg border border-gray-700 bg-gray-800 px-4 py-3 text-white placeholder-gray-500 focus:border-orange-500 focus:outline-none"
                      placeholder="Enter service title"
                      required
                    />
                  </div>

                  <div>
                    <label className="mb-2 block text-sm font-medium text-gray-300">Category</label>
                    <select
                      value={formData.category}
                      onChange={(e) => setFormData((current) => ({ ...current, category: e.target.value, subtitle: e.target.value }))}
                      className="w-full rounded-lg border border-gray-700 bg-gray-800 px-4 py-3 text-white focus:border-orange-500 focus:outline-none"
                    >
                      {categoryOptions.map((category) => (
                        <option key={category} value={category}>
                          {category}
                        </option>
                      ))}
                    </select>
                  </div>

                </div>

                <div>
                  <label className="mb-2 flex items-center gap-2 text-sm font-medium text-gray-300">
                    <AlignLeft size={16} />
                    Description
                  </label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => setFormData((current) => ({ ...current, description: e.target.value }))}
                    className="min-h-[110px] w-full resize-none rounded-lg border border-gray-700 bg-gray-800 px-4 py-3 text-white placeholder-gray-500 focus:border-orange-500 focus:outline-none"
                    placeholder="Enter service description"
                    required
                  />
                </div>

                <div>
                  <label className="mb-2 flex items-center gap-2 text-sm font-medium text-gray-300">
                    <ImageIcon size={16} />
                    Upload Image
                  </label>
                  <div className="space-y-3">
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleImageUpload}
                      disabled={uploadingImage}
                      className="w-full cursor-pointer rounded-lg border border-gray-700 bg-gray-800 px-4 py-3 text-gray-300 file:mr-4 file:rounded-lg file:border-0 file:bg-orange-500 file:px-4 file:py-2 file:text-sm file:font-medium file:text-white hover:file:bg-orange-600 disabled:cursor-not-allowed disabled:opacity-50"
                    />
                    {uploadingImage && <div className="text-sm text-orange-400">Uploading...</div>}
                    {formData.image && (
                      <img
                        src={formData.image.startsWith('blob:') ? formData.image : assetUrl(formData.image)}
                        alt="Preview"
                        className="h-32 w-32 rounded-lg border border-gray-700 object-cover"
                      />
                    )}
                  </div>
                </div>

                <div>
                  <label className="mb-2 block text-sm font-medium text-gray-300">Features</label>
                  <div className="space-y-3">
                    <div className="flex gap-2">
                      <input
                        type="text"
                        value={newFeature}
                        onChange={(e) => setNewFeature(e.target.value)}
                        className="flex-1 rounded-lg border border-gray-700 bg-gray-800 px-4 py-2 text-white placeholder-gray-500 focus:border-orange-500 focus:outline-none"
                        placeholder="Add feature"
                      />
                      <button
                        type="button"
                        onClick={handleAddFeature}
                        className="rounded-lg bg-blue-500 px-4 py-2 text-white transition-colors hover:bg-blue-600"
                      >
                        Add
                      </button>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {formData.features.map((feature, index) => (
                        <span key={`${feature}-${index}`} className="flex items-center gap-2 rounded-lg bg-blue-500/20 px-3 py-1 text-blue-400">
                          {feature}
                          <button type="button" onClick={() => handleRemoveFeature(index)} className="text-blue-300 hover:text-blue-100">
                            <X size={14} />
                          </button>
                        </span>
                      ))}
                    </div>
                  </div>
                </div>

                <div className="flex items-center gap-3">
                  <input
                    type="checkbox"
                    id="isActive"
                    checked={formData.isActive}
                    onChange={(e) => setFormData((current) => ({ ...current, isActive: e.target.checked }))}
                    className="h-4 w-4 rounded border-gray-600 bg-gray-800 text-orange-500 focus:ring-orange-500"
                  />
                  <label htmlFor="isActive" className="text-sm text-gray-300">
                    Active (show on website)
                  </label>
                </div>

                <div className="flex gap-3 pt-4">
                  <button
                    type="button"
                    onClick={() => setIsModalOpen(false)}
                    className="flex-1 rounded-lg border border-gray-700 bg-gray-800 px-4 py-3 text-gray-300 transition-colors hover:bg-gray-700"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="flex flex-1 items-center justify-center gap-2 rounded-lg bg-orange-500 px-4 py-3 text-white transition-colors hover:bg-orange-600"
                  >
                    <Save size={18} />
                    {editingService ? 'Update' : 'Create'}
                  </button>
                </div>
              </form>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default ModernServicesEditor;
